import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Heart, Star, Loader2, TrendingUp, Share2, Instagram, Facebook, ExternalLink, CheckCircle2, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function FeedbackDashboard() {
  const [dash, setDash] = useState(null);
  const [publicar, setPublicar] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [d, p] = await Promise.all([
        api.get("/feedback/dashboard"),
        api.get("/feedback/listo-publicar"),
      ]);
      setDash(d.data);
      setPublicar(p.data);
    } catch (e) {
      toast.error("Error cargando feedback");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="p-10 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;
  }
  if (!dash) return null;

  const m = dash.metricas;

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="feedback-dashboard">
      <PageHeader title="Feedback de clientes" subtitle="Encuestas de satisfacción post-montaje y autorizaciones de publicación" icon={Heart} />

      {/* Métricas globales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="NPS Score" value={`${m.nps_score}`} suffix="" tint={npsColor(m.nps_score)} testid="metric-nps" />
        <Metric label="Satisfacción media" value={m.satisfaccion_avg} suffix="/5 ⭐" testid="metric-sat" />
        <Metric label="Respuestas" value={m.total_respuestas} testid="metric-total" />
        <Metric label="Plazo cumplido" value={`${m.plazo_cumplido_pct}%`} testid="metric-plazo" />
        <MiniMetric label="Promotores" value={m.promotores} color="text-emerald-400" testid="metric-promotores" />
        <MiniMetric label="Pasivos" value={m.pasivos} color="text-amber-400" testid="metric-pasivos" />
        <MiniMetric label="Detractores" value={m.detractores} color="text-rose-400" testid="metric-detractores" />
        <MiniMetric label="Pendientes responder" value={m.pendientes_responder} color="text-zinc-400" testid="metric-pendientes" />
      </div>

      <Tabs defaultValue="respuestas">
        <TabsList>
          <TabsTrigger value="respuestas" data-testid="tab-respuestas">📊 Respuestas ({dash.respuestas.length})</TabsTrigger>
          <TabsTrigger value="publicar" data-testid="tab-publicar">📤 Listos para publicar ({publicar?.total || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="respuestas">
          {dash.respuestas.length === 0 ? (
            <Empty msg="Aún no hay respuestas." />
          ) : (
            <div className="space-y-2 mt-4">
              {dash.respuestas.map((r) => (
                <RespuestaCard key={r.peticion_id} r={r} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="publicar">
          {!publicar?.items?.length ? (
            <Empty msg="Aún no hay autorizaciones de publicación." />
          ) : (
            <div className="space-y-3 mt-4">
              {publicar.items.map((p) => (
                <PublicarCard key={p.peticion_id} p={p} onMark={() => load()} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function npsColor(score) {
  if (score >= 50) return "text-emerald-400";
  if (score >= 0) return "text-amber-400";
  return "text-rose-400";
}

function Metric({ label, value, suffix, tint, testid }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4" data-testid={testid}>
      <div className="text-xs text-zinc-500 mb-1">{label}</div>
      <div className={`text-2xl font-bold ${tint || "text-zinc-100"}`}>{value}<span className="text-sm font-normal text-zinc-500">{suffix}</span></div>
    </div>
  );
}

function MiniMetric({ label, value, color, testid }) {
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3" data-testid={testid}>
      <div className="text-[10px] text-zinc-500 uppercase tracking-wide">{label}</div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
}

function Empty({ msg }) {
  return <div className="text-center text-zinc-500 py-12 text-sm">{msg}</div>;
}

function RespuestaCard({ r }) {
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4 hover:bg-zinc-900/60 transition" data-testid={`respuesta-${r.peticion_id}`}>
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <div className="flex items-center gap-2">
            <Link to={`/peticiones/${r.peticion_id}`} className="font-mono text-xs text-amber-400 hover:underline">{r.codigo}</Link>
            <span className="text-zinc-300 text-sm">{r.cliente}</span>
            {r.autoriza_publicacion && <span className="px-1.5 py-0.5 rounded text-[10px] bg-pink-500/20 text-pink-300 border border-pink-500/40">📤 Publicar</span>}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">{r.nombre}</div>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-1 justify-end">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className={`w-3.5 h-3.5 ${i < (r.satisfaccion || 0) ? "text-amber-400 fill-amber-400" : "text-zinc-700"}`} />
            ))}
          </div>
          <div className="text-xs text-zinc-400 mt-1">NPS <span className={`font-bold ${r.nps >= 9 ? "text-emerald-400" : r.nps >= 7 ? "text-amber-400" : "text-rose-400"}`}>{r.nps}</span>/10</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 text-[10px] text-zinc-400 mt-2">
        {r.cumplimos_plazo === true && <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">✅ Plazo OK</span>}
        {r.cumplimos_plazo === false && <span className="px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300">❌ Plazo</span>}
        {r.profesionalidad && <span>👨‍🔧 Profesionalidad {r.profesionalidad}/5</span>}
        {r.calidad && <span>🎨 Calidad {r.calidad}/5</span>}
        {r.repetirias === "si" && <span className="text-emerald-300">🟢 Repetiría</span>}
        {r.repetirias === "probable" && <span className="text-amber-300">🟡 Probable</span>}
        {r.repetirias === "no" && <span className="text-rose-300">🔴 No repetiría</span>}
      </div>
      {r.comentarios && (
        <div className="mt-2 text-xs italic text-zinc-400 bg-zinc-950/50 rounded p-2 border-l-2 border-amber-500/40">
          "{r.comentarios}"
        </div>
      )}
    </div>
  );
}

function PublicarCard({ p, onMark }) {
  const [marking, setMarking] = useState(false);

  const marcar = async () => {
    setMarking(true);
    try {
      await api.post(`/feedback/${p.peticion_id}/marcar-publicado`);
      toast.success("Marcado como publicado");
      onMark();
    } catch (e) {
      toast.error("Error");
    } finally { setMarking(false); }
  };

  const desmarcar = async () => {
    setMarking(true);
    try {
      await api.post(`/feedback/${p.peticion_id}/desmarcar-publicado`);
      toast.success("Desmarcado");
      onMark();
    } catch (e) {
      toast.error("Error");
    } finally { setMarking(false); }
  };

  return (
    <div className={`bg-zinc-900/40 border ${p.publicado ? "border-emerald-500/40" : "border-pink-500/30"} rounded-xl p-4`} data-testid={`publicar-${p.peticion_id}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {p.cliente_logo && <img src={p.cliente_logo} alt="" className="w-10 h-10 rounded bg-white p-1 object-contain" />}
          <div className="min-w-0">
            <Link to={`/peticiones/${p.peticion_id}`} className="font-mono text-xs text-amber-400 hover:underline">{p.codigo}</Link>
            <div className="text-zinc-200 text-sm font-semibold truncate">{p.cliente}</div>
            <div className="text-xs text-zinc-500 truncate">{p.nombre}</div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="flex items-center gap-1 justify-end">
            <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
            <span className="text-xs text-zinc-300">{p.satisfaccion}/5</span>
          </div>
          <div className="text-[10px] text-zinc-500">NPS {p.nps}/10</div>
        </div>
      </div>

      {p.comentarios && (
        <div className="text-xs italic text-zinc-400 bg-zinc-950/50 rounded p-2 border-l-2 border-pink-500/40 mb-3">"{p.comentarios}"</div>
      )}

      {/* Autorización & socials */}
      <div className="flex flex-wrap gap-2 text-[10px] mb-3">
        <span className="px-2 py-0.5 rounded bg-pink-500/20 text-pink-300 border border-pink-500/40 font-semibold">📤 Autorizado a publicar</span>
        {p.autoriza_etiquetar && <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/40">🏷 Etiquetar OK</span>}
        {p.social_instagram && (
          <a href={`https://instagram.com/${p.social_instagram}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-800 text-pink-300 hover:bg-zinc-700">
            <Instagram className="w-3 h-3" /> @{p.social_instagram}
          </a>
        )}
        {p.social_facebook && (
          <a href={`https://facebook.com/${p.social_facebook}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-800 text-blue-300 hover:bg-zinc-700">
            <Facebook className="w-3 h-3" /> {p.social_facebook}
          </a>
        )}
        {p.social_tiktok && (
          <a href={`https://tiktok.com/@${p.social_tiktok}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 hover:bg-zinc-700">
            🎵 @{p.social_tiktok}
          </a>
        )}
      </div>

      {/* Fotos preview */}
      {p.n_fotos > 0 ? (
        <div className="flex gap-1 mb-3 overflow-x-auto">
          {p.fotos.map((f, i) => (
            <img key={i} src={f.url} alt="" className="w-16 h-16 rounded object-cover shrink-0" />
          ))}
          {p.n_fotos > p.fotos.length && (
            <div className="w-16 h-16 rounded bg-zinc-800 flex items-center justify-center text-xs text-zinc-400 shrink-0">+{p.n_fotos - p.fotos.length}</div>
          )}
        </div>
      ) : (
        <div className="text-[10px] text-amber-400 mb-3 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> Sin fotos aprobadas aún. Aprueba fotos en el detalle para usarlas.
        </div>
      )}

      <div className="flex gap-2">
        {p.publicado ? (
          <>
            <Button onClick={desmarcar} disabled={marking} variant="outline" size="sm" data-testid={`btn-desmarcar-${p.peticion_id}`} className="text-xs">
              <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-400" /> Publicado — desmarcar
            </Button>
          </>
        ) : (
          <Button onClick={marcar} disabled={marking} size="sm" data-testid={`btn-marcar-publicado-${p.peticion_id}`} className="bg-pink-500 hover:bg-pink-600 text-white text-xs">
            <Share2 className="w-3 h-3 mr-1" /> Marcar como publicado
          </Button>
        )}
        <Link to={`/peticiones/${p.peticion_id}`}>
          <Button variant="outline" size="sm" className="text-xs">
            <ExternalLink className="w-3 h-3 mr-1" /> Ver petición
          </Button>
        </Link>
      </div>
    </div>
  );
}
